import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'ws-scripts/modules/common';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import useSearchPanel, { SEARCH_PANEL_ALIAS } from '../hooks/useSearchPanel';
import { DRILLDOWN_DIALOG_ID } from '../global-constants';

/**
 * SearchFieldButton — button styled as the drawer's search field.
 *
 * Not a text input (PRD D5): a read-only input would still be announced as a
 * textbox, which contradicts the requirement that it not accept typed text.
 * Activating it opens the same Level 2 drilldown panel a category trigger
 * opens, via the shared useSearchPanel hook (PRD D8), carrying the same
 * expanded/controls/haspopup semantics as SubMenuTrigger.
 *
 * @param {SearchFieldButtonProps} props
 * @param {ObjectElementRef} [props.searchRef] - Ref forwarded to the button element.
 *   Also passed as the drilldown's trigger ref, so focus returns here when the
 *   panel closes. Threaded from the drawer root in T4 so the header magnifier
 *   can share it; falls back to a locally-owned ref so this component is
 *   correct standalone.
 */
const SearchFieldButton = ({ searchRef }) => {
	const localRef = useRef(null);
	const buttonRef = searchRef || localRef;
	const labels = useLabels();
	const { activeDrillDownItem } = useDrawerNavigation();
	const openSearchPanel = useSearchPanel();
	const placeholder = labels.get('SEARCH_THE_WHOLE_SITE');
	const isPanelOpen = activeDrillDownItem?.id === SEARCH_PANEL_ALIAS;

	return (
		<div
			className="ws-nav-drawer__search-input d-flex align-items-center align-self-stretch m-4 py-3 px-4"
			data-testid="drawer-search-wrapper"
		>
			<button
				type="button"
				ref={buttonRef}
				className="ws-nav-drawer__search-input-field border-0 bg-transparent p-0 mr-4 ddc-font-size-large"
				aria-expanded={isPanelOpen}
				aria-controls={DRILLDOWN_DIALOG_ID}
				aria-haspopup="dialog"
				onClick={() => openSearchPanel(buttonRef)}
				data-testid="drawer-search-trigger"
			>
				{placeholder}
			</button>
			<span
				className="ddc-icon ddc-icon-search-thin ws-nav-drawer__search-input-icon ddc-font-size-xlarge"
				data-testid="drawer-search-icon"
				aria-hidden="true"
			/>
		</div>
	);
};

SearchFieldButton.propTypes = {
	searchRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

SearchFieldButton.defaultProps = {
	searchRef: null
};

export default SearchFieldButton;
